import { FormikErrors } from 'formik';
import { useCallback } from 'react';

import { ValidationResponse } from '../../../types/ValidationResponse';
import { validateOffer } from '../../common/api/clients/order.api';
import { queries } from '../../common/api/queryKeys/queries';
import { toNullableNumber } from '../../common/helpers/toNullableNumber.helper';
import { mapValidationResponseToFormikErrors } from '../../common/validation/mapValidationResponseToFormikErrors';
import { useBackendValidationRequest } from '../../common/validation/useBackendValidationRequest';
import { OfferModel } from '../model/OfferModel';

interface ValidateOfferRequest {
    offerId: string;
    offer: {
        state: OfferModel['state'];
        vendorReference: string | undefined;
        cancelReason: OfferModel['cancelReason'];
        paymentTermReference: OfferModel['paymentTermReference'];
        paymentTermReferenceDays: number | null;
        supplyMode: OfferModel['supplyMode'];
        noticeDays: number | null;
        portId: OfferModel['portId'];
        dateDelivery: OfferModel['dateDelivery'];
        agentId: string | undefined;
        buyerReference: string | undefined;
        vesselId: OfferModel['vesselId'];
        items: Array<{
            productId: string | undefined;
            price: { value: number; currency: string } | undefined;
            packType: string | undefined;
            unitSize: number | null;
            units: number | null;
            unit: string | undefined;
            deleted: boolean;
            volume: number | null;
        }>;
        surcharges: Array<{
            name: string | undefined;
            value: { value: number; currency: string } | undefined;
            deleted: boolean;
            surchargeType: string | undefined;
        }>;
        samplekits:
            | Array<{
                  name: string | undefined;
                  quantity: number | null;
                  value: { value: number; currency: string } | undefined;
                  deleted: boolean;
              }>
            | undefined;
        fileIds: OfferModel['fileIds'];
    };
}

export function useValidateOfferForm(): (formikValues: OfferModel) => Promise<FormikErrors<OfferModel>> {
    const requestValidation = useBackendValidationRequest<ValidateOfferRequest, ValidationResponse>({
        queryKeyPrefix: queries.backendValidation.offerLubes.queryKey,
        request: ({ offerId, offer }) => validateOffer({ offerId, offer }),
    });

    return useCallback(
        async (formikValues: OfferModel): Promise<FormikErrors<OfferModel>> => {
            const validationRequest: ValidateOfferRequest['offer'] = {
                state: formikValues.state,
                vendorReference: formikValues.vendorReference || undefined,
                cancelReason: formikValues.cancelReason,
                paymentTermReference: formikValues.paymentTermReference,
                noticeDays: toNullableNumber(formikValues.noticeDays),
                paymentTermReferenceDays: toNullableNumber(formikValues.paymentTermReferenceDays),
                supplyMode: formikValues.supplyMode,
                portId: formikValues.portId,
                dateDelivery: formikValues.dateDelivery,
                agentId: formikValues.agentId || undefined,
                buyerReference: formikValues.buyerReference || undefined,
                vesselId: formikValues.vesselId,
                items: formikValues.items.map((item) => {
                    const priceValue = toNullableNumber(item.price?.value);

                    return {
                        productId: item.productId || undefined,
                        price:
                            priceValue !== null
                                ? { value: priceValue, currency: item.price?.currency || 'USD' }
                                : undefined,
                        packType: item.packType,
                        units: toNullableNumber(item.units),
                        unitSize: toNullableNumber(item.unitSize),
                        unit: item.unit,
                        deleted: item.deleted,
                        volume: toNullableNumber(item.volume),
                    };
                }),
                samplekits: formikValues.samplekits?.map((samplekit) => {
                    const value = toNullableNumber(samplekit.value?.value);

                    return {
                        name: samplekit.name,
                        quantity: toNullableNumber(samplekit.quantity),
                        value: value !== null ? { value, currency: samplekit.value?.currency || 'USD' } : undefined,
                        deleted: samplekit.deleted,
                    };
                }),
                surcharges: formikValues.surcharges.map((surcharge) => {
                    const value = toNullableNumber(surcharge.value?.value);

                    return {
                        name: surcharge.name,
                        value: value !== null ? { value, currency: 'USD' } : undefined,
                        deleted: surcharge.deleted,
                        surchargeType: surcharge.surchargeType,
                    };
                }),
                fileIds: formikValues.fileIds,
            };
            const validationKey = JSON.stringify({
                offerId: formikValues.id,
                validationRequest,
            });

            try {
                const response = await requestValidation({
                    validationKey,
                    params: {
                        offerId: formikValues.id,
                        offer: validationRequest,
                    },
                });

                return mapValidationResponseToFormikErrors<OfferModel>(response);
            } catch {
                return {};
            }
        },
        [requestValidation]
    );
}
